/**
 * The outreach report's arithmetic, in the browser.
 *
 * The server sends COUNTS keyed by wave, heads/others and which email of the series
 * (functions/outreach/cube.js). Everything here is a pure function of those counts and the
 * page's filters, so changing a filter redraws at once and asks the server nothing.
 *
 * Three rules this file keeps:
 *
 * 1. A FIGURE IS NEVER SPLIT WHERE ITS SOURCE IS NOT. Emails 2 and 3 go to heads and others
 *    together. With "Heads" chosen, HubSpot's per-email figures and the visits from those
 *    emails cannot be divided, so they are left OUT and counted as `unsplit`, and the page
 *    says so. Nothing is apportioned.
 *
 * 2. GROUPS OF DIFFERENT SIZES ARE COMPARED BY RATE, WITH A RANGE (Lee, 6 Oct 2026: "not all
 *    waves are the same size"). Wave 1 had 1,000 people and wave 5 had 1,500: more opens is
 *    not a better wave. `compare` gives each group its rate of emails delivered and the range
 *    that rate could lie in by chance alone (Wilson, 95%); a group "stands apart" only when
 *    its range does not touch the range of all the others together.
 *
 * 3. "PEOPLE" IS NEVER GIVEN WITHOUT "CANNOT TELL". A visit is a person on the evidence, a
 *    machine, or cannot be told (functions/outreach/visitors.js). `visits` always carries
 *    all three.
 *
 * 4. EACH RECIPIENT'S OWN EVENTS ARE USED WHERE THEY WERE READ (`cube.recipients`). They are
 *    counted by the CONTACT's wave and role, so they split exactly where HubSpot's per-email
 *    totals cannot: emails 2 and 3 by heads and others, and the email that went to two waves by
 *    wave. Where they were not read, HubSpot's totals are used and rule 1 applies to them.
 *
 * 5. THE READERS AN OPEN COUNT CANNOT SEE ARE GIVEN AS A COUNT, FOR THE WHOLE CAMPAIGN
 *    (`readersOf`). Totals by kind of visitor and outcome: never by wave or email, whatever is
 *    chosen. A SHARE of readers is drawn from that count only where its check passes.
 *
 * 6. AN ESTIMATE IS ALWAYS MARKED AS ONE, AND IS NEVER ADDED TO A COUNT (`estimatesOf`; Lee,
 *    7 Oct 2026: "we'd rather have estimates that are more likely than lower numbers that are
 *    more provable … but in such a way that it's clear how estimated they are"). The estimate
 *    of readers corrects BOTH ways: down, by counting only opens made by a person's own mail
 *    program; up, by the share of people who demonstrably had the email open and had such an
 *    open. It is given as a range first, says what it rests on and what it assumes, lowers its
 *    own confidence when a check on it fails, and says it is UNTESTED until a seed test exists.
 *    A one-sided "N readers for every open" is what the review of PR #59 removed; this is not it.
 */

export const BANDS = ['person', 'unsure', 'machine'];
export const MACHINE_KINDS = ['twin', 'datacentre', 'automated', 'abroad'];
export const LAG_BUCKETS = ['0-2m', '2-10m', '10-60m', '1-24h', '1-7d', '7d+'];
export const VISIT_MEASURES = ['n', 'read15', 'read60', 'secondPage', 'cookies', 'slider', 'cta', 'video', 'quote', 'declined'];

/** What happened to each delivered email, from the recipient's own events (recipients.js). */
export const SEND_KEYS = ['delivered', 'openedAny', 'opened', 'program', 'browser', 'apple', 'machineOnly', 'read', 'skimmed', 'glanced', 'untimed',
  'openedAgain', 'clickedAny', 'clicked', 'go', 'direct', 'other', 'clickedNoOpen', 'unsubscribeLink', 'scanned',
  'fateRead', 'fateSkimmed', 'fateGlanced', 'fateUntimed', 'fateClickOnly', 'fateMachine', 'fateNone',
  'clickProgram', 'clickApple', 'clickBrowser', 'clickAtClick', 'clickAfter', 'clickMachine', 'clickNone'];
/** Every email HubSpot counts as clicked has exactly one of these: had a picture loaded at the click? */
export const CLICK_PICTURES = ['clickProgram', 'clickApple', 'clickBrowser', 'clickAtClick', 'clickAfter', 'clickMachine', 'clickNone'];
/** Every delivery has exactly one of these, so they add up to the deliveries. */
export const FATES = ['fateRead', 'fateSkimmed', 'fateGlanced', 'fateUntimed', 'fateClickOnly', 'fateMachine', 'fateNone'];
export const PERSON_KEYS = ['people', 'openedAny', 'opened', 'opened1', 'opened2', 'opened3', 'read', 'clicked', 'clicked2', 'scanned'];
/** A visit matched to the click that led to it: had the email loaded a picture? (readers.js) */
export const READER_OUTCOMES = ['program', 'apple', 'browser', 'atClick', 'after', 'machine', 'none', 'ambiguous', 'unmatched'];
/** Had a picture loaded by then? The same seven answers for a click and for an unsubscribe. */
export const PICTURE_OUTCOMES = ['program', 'apple', 'browser', 'atClick', 'after', 'machine', 'none'];
/** How someone unsubscribed: by the link in the email, their mail program's button, or neither. */
export const UNSUB_HOWS = ['link', 'button', 'other'];
export const READER_GAPS = ['before', '0-2s', '2-5s', '5-15s', '15-30s', 'near-miss', '2-10m', 'further', 'no-click'];
/** Fewer matched visits by people than this, and no share of readers is drawn from them. */
export const MIN_READERS = 8;
/** Fewer matched machine visits than this, and the check cannot be run. */
export const MIN_CHECK = 20;
/**
 * A scanner clicks before anyone could have read the email, so "a person's mail program had
 * loaded a picture before the click" should be close to never for machines. Above this, the
 * kinds of visitor or the matching are wrong, however much higher the people's figure is.
 */
export const CHECK_CEILING = 0.1;

/** Too few to say anything about a rate: shown, greyed, never ranked. */
export const SMALL_BASE = 30;

export const DEFAULT_FILTERS = { waves: [], role: 'all', step: 'all' };

/**
 * The range a rate could lie in, given how few it rests on (Wilson score interval).
 * 3 of 20 is "15%", but could as easily be 5% or 36%; 150 of 1,000 is 13% to 17%.
 * @returns {{ rate, low, high }} or null when there is nothing to divide by
 */
export function wilson(k, n, z = 1.96) {
  if (!n || n <= 0 || k == null) return null;
  const p = Math.min(Math.max(k / n, 0), 1);
  const z2 = z * z;
  const denom = 1 + z2 / n;
  const centre = (p + z2 / (2 * n)) / denom;
  const half = (z * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n))) / denom;
  return { rate: p, low: Math.max(0, centre - half), high: Math.min(1, centre + half) };
}

const add = (into, row, keys) => { keys.forEach((k) => { into[k] = (into[k] || 0) + (row[k] || 0); }); return into; };
const zero = (keys) => Object.fromEntries(keys.map((k) => [k, 0]));
const sumNullable = (rows, key) => (rows.some((r) => r[key] != null) ? rows.reduce((a, r) => a + (r[key] || 0), 0) : null);

/**
 * The shape of the stored figures this page reads (functions/outreach/cube.js VERSION).
 * ⚠️ ONE version, never "this one or an older one". A figure an older shape does not have
 * would be read as 0, and a zero made of absence is a false finding. Anything else is not
 * drawn at all: the page says the figures could not be read, and an admin builds them again.
 */
export const CUBE_VERSION = 3;

const list = (v) => (Array.isArray(v) ? v : []);
const count = (v) => (Number.isFinite(v) ? v : 0);
const zeroCheck = () => ({ visits: 0, withFetchBefore: 0, expectedByChance: 0 });
const emptyHours = () => [0, 1, 2, 3, 4, 5, 6].map(() => new Array(24).fill(0));

/**
 * The stored figures, made safe to draw: or null when they cannot be read at all.
 *
 * The page is handed whatever is in the stored document. That is normally what the build
 * wrote, but it could be an older shape, an empty object, or something written by hand (an
 * admin's browser can write the collection). Without this the page threw on a document with no
 * `returning` and the whole app's error screen took over (the independent review of PR #58).
 *
 *   null     not an object, a different version, or without its three main lists: the page
 *            says the figures could not be read, and an admin can rebuild them
 *   a cube   every other part given its empty default, so a missing part is zeros, never a crash
 */
export function usableCube(cube) {
  if (!cube || typeof cube !== 'object' || Array.isArray(cube)) return null;
  if (cube.version !== CUBE_VERSION) return null;
  if (!Array.isArray(cube.emails) || !Array.isArray(cube.people) || !Array.isArray(cube.visits)) return null;
  const rows = (v) => list(v).filter((r) => r && typeof r === 'object');
  const p = cube.pictures && typeof cube.pictures === 'object' ? cube.pictures : {};
  const hoursOk = Array.isArray(p.hours) && p.hours.length === 7 && p.hours.every((d) => Array.isArray(d) && d.length === 24 && d.every((n) => Number.isFinite(n)));
  const r = cube.returning && typeof cube.returning === 'object' ? cube.returning : {};
  const n = cube.noEmailId && typeof cube.noEmailId === 'object' ? cube.noEmailId : {};
  const c = cube.pictureCheck && typeof cube.pictureCheck === 'object' ? cube.pictureCheck : {};
  const check = (b) => (c[b] && typeof c[b] === 'object' ? { visits: count(c[b].visits), withFetchBefore: count(c[b].withFetchBefore), expectedByChance: count(c[b].expectedByChance) } : zeroCheck());
  // Each recipient's events, counted (null when that part was not read on the build).
  const rc = cube.recipients && typeof cube.recipients === 'object' && Array.isArray(cube.recipients.sends) && Array.isArray(cube.recipients.persons) ? cube.recipients : null;
  const ev = rc && rc.events && typeof rc.events === 'object' ? rc.events : {};
  // A split by timing, or null when it was not stored: never zeros made of absence.
  const splitOf = (v) => (v && typeof v === 'object' && v.early && typeof v.early === 'object' && v.later && typeof v.later === 'object'
    ? { early: { n: count(v.early.n), seen: count(v.early.seen) }, later: { n: count(v.later.n), seen: count(v.later.seen) }, untimed: count(v.untimed) } : null);
  const bs = rc && Array.isArray(rc.unsubs) && rc.base && typeof rc.base === 'object' && Number.isFinite(rc.base.n) ? rc.base : null;
  const fm = bs && bs.firms && typeof bs.firms === 'object' ? bs.firms : null;
  const rcHoursOk = rc && Array.isArray(rc.hours) && rc.hours.length === 7 && rc.hours.every((d) => Array.isArray(d) && d.length === 24 && d.every((x) => Number.isFinite(x)));
  const recipients = rc ? {
    sends: rows(rc.sends),
    persons: rows(rc.persons),
    series: rows(rc.series).filter((x) => typeof x.pattern === 'string' && (x.what === 'opened' || x.what === 'clicked')),
    hours: rcHoursOk ? rc.hours : emptyHours(),
    events: { delivered: count(ev.delivered), opens: count(ev.opens), clicks: count(ev.clicks), statusChanges: count(ev.statusChanges) },
    cut: rows(rc.cut),
    unknownRecipients: count(rc.unknownRecipients),
    // who unsubscribed: a list when it was read (empty: nobody did), null when it was not
    unsubs: Array.isArray(rc.unsubs) ? rows(rc.unsubs).filter((x) => UNSUB_HOWS.includes(x.how) && PICTURE_OUTCOMES.includes(x.outcome) && Number.isFinite(x.n)) : null,
    // The base the estimate is scaled from, as totals for the whole campaign: its numbers and
    // nothing else that an object handed in might carry. null when who unsubscribed was not read.
    base: bs ? {
      n: count(bs.n), seenProgram: count(bs.seenProgram), seenProgramOrApple: count(bs.seenProgramOrApple), seenKeptOpen: count(bs.seenKeptOpen),
      timing: splitOf(bs.timing),
      firms: fm ? { groups: count(fm.groups), design: Number.isFinite(fm.design) && fm.design >= 1 ? fm.design : null, leftOut: count(fm.leftOut) } : null,
      ownLeftOut: count(bs.ownLeftOut),
    } : null,
    clickTiming: splitOf(rc.clickTiming),
  } : null;
  // Visits matched to the click that led to them (null when the events were not read).
  const rd = cube.readers && typeof cube.readers === 'object' && Array.isArray(cube.readers.rows) ? cube.readers : null;
  const readers = rd ? {
    // Only the kind of visitor, the outcome and the number are taken: the figures are totals for
    // the whole campaign, and anything else a row might carry (an email, a wave) is not read.
    rows: rows(rd.rows).filter((x) => BANDS.includes(x.band) && READER_OUTCOMES.includes(x.outcome) && Number.isFinite(x.n)).map((x) => ({ band: x.band, outcome: x.outcome, n: x.n })),
    gaps: rows(rd.gaps).filter((x) => BANDS.includes(x.band) && READER_GAPS.includes(x.bucket) && Number.isFinite(x.n)).map((x) => ({ band: x.band, bucket: x.bucket, n: x.n })),
    clicksRead: count(rd.clicksRead),
  } : null;
  return {
    ...cube,
    recipients,
    readers,
    builtAt: typeof cube.builtAt === 'string' ? cube.builtAt : null,
    waves: rows(cube.waves),
    emails: rows(cube.emails),
    people: rows(cube.people),
    visits: rows(cube.visits),
    visitTiming: rows(cube.visitTiming),
    visitDays: rows(cube.visitDays),
    visitPages: rows(cube.visitPages).filter((x) => typeof x.page === 'string'),
    goHits: rows(cube.goHits),
    returning: { recognised: count(r.recognised), cameBack: count(r.cameBack), onALaterEmail: count(r.onALaterEmail) },
    noEmailId: { visits: count(n.visits), team: count(n.team) },
    pictures: {
      since: typeof p.since === 'string' ? p.since : null,
      days: rows(p.days).filter((d) => typeof d.day === 'string').map((d) => ({ day: d.day, program: count(d.program), apple: count(d.apple), scanner: count(d.scanner), preview: count(d.preview) })),
      programs: p.programs && typeof p.programs === 'object' ? Object.fromEntries(Object.entries(p.programs).filter(([, v]) => Number.isFinite(v))) : {},
      hours: hoursOk ? p.hours : emptyHours(),
    },
    pictureCheck: { person: check('person'), unsure: check('unsure'), machine: check('machine') },
    notes: list(cube.notes).filter((x) => typeof x === 'string'),
    notRead: rows(cube.notRead).filter((x) => typeof x.what === 'string'),
    calls: cube.calls && Number.isFinite(cube.calls.used) && Number.isFinite(cube.calls.ceiling) ? { used: cube.calls.used, ceiling: cube.calls.ceiling } : null,
  };
}

/** Filters as the model reads them: a list of waves (empty = all), a role, a step. */
export function normaliseFilters(filters) {
  const f = { ...DEFAULT_FILTERS, ...(filters || {}) };
  const step = f.step === 'all' || f.step == null ? 'all' : Number(f.step);
  return {
    waves: [...new Set((f.waves || []).map(Number).filter((w) => Number.isFinite(w)))].sort((a, b) => a - b),
    role: f.role === 'head' || f.role === 'other' ? f.role : 'all',
    step: Number.isFinite(step) ? step : 'all',
  };
}

/** The waves a row belongs to: one; several TOGETHER (an email sent to two waves at once); or none. */
export const wavesOfRow = (row) => (Array.isArray(row.waves) && row.waves.length ? row.waves : row.wave != null ? [row.wave] : []);

/**
 * Does a row belong to the filters?
 *   'yes' | 'no'
 *   'role'  it is for heads and others together, and one of them is chosen
 *   'wave'  it is for several waves together, and only some of them are chosen
 * The last two are "unsplit": the row is left OUT and counted as left out. Never apportioned.
 */
function place(row, f) {
  let byWave = false;
  if (f.waves.length) {
    const mine = wavesOfRow(row);
    const inside = mine.filter((w) => f.waves.includes(w)).length;
    if (!inside) return 'no';
    byWave = inside < mine.length;
  }
  if (f.step !== 'all' && row.step !== f.step) return 'no';
  if (f.role !== 'all' && row.role !== f.role) return row.role === 'both' || row.role == null ? 'role' : 'no';
  return byWave ? 'wave' : 'yes';
}
const unsplit = (where) => where === 'role' || where === 'wave';

/** A recipient's row carries the CONTACT's own wave and role, so it is in or out: never unsplit. */
function exact(row, f) {
  if (f.waves.length && !f.waves.includes(row.wave)) return false;
  if (f.role !== 'all' && row.role !== f.role) return false;
  return f.step === 'all' || row.step === undefined || row.step === f.step;
}

/** The waves, each with whether any email has gone to it. */
export function wavesOf(cube) {
  return ((cube && cube.waves) || []).map((w) => ({ ...w, total: (w.heads || 0) + (w.others || 0), sent: (w.emailsSent || 0) > 0 }));
}

/** The emails of the series that have been sent: 1, 2, 3… */
export function stepsOf(cube) {
  const steps = new Set(((cube && cube.emails) || []).filter((e) => e.series === 'outreach' && e.step != null).map((e) => e.step));
  return [...steps].sort((a, b) => a - b);
}

/**
 * Every figure for one set of filters.
 */
export function select(cube, filters) {
  const f = normaliseFilters(filters);
  const c = cube || {};
  const outreach = (c.emails || []).filter((e) => e.series === 'outreach' && wavesOfRow(e).length && e.step != null);

  // ── HubSpot's per-email figures ────────────────────────────────────────────────────────
  const mine = outreach.filter((e) => place(e, f) === 'yes');
  const unsplitEmails = outreach.filter((e) => unsplit(place(e, f)));
  const left = { emailsByRole: outreach.filter((e) => place(e, f) === 'role').length, emailsByWave: outreach.filter((e) => place(e, f) === 'wave').length, visitsByRole: 0, visitsByWave: 0 };
  const sent = {
    emails: mine.length,
    sent: sumNullable(mine, 'sent'),
    delivered: sumNullable(mine, 'delivered'),
    opened: sumNullable(mine, 'opened'),
    clicked: sumNullable(mine, 'clicked'),
    bounced: sumNullable(mine, 'bounced'),
    unsubscribed: sumNullable(mine, 'unsubscribed'),
    spam: sumNullable(mine, 'spam'),
    replied: sumNullable(mine, 'replied'),
    // "Still counting" is about every email these filters touch, divided or not.
    counting: outreach.some((e) => place(e, f) !== 'no' && e.counting),
    touching: outreach.filter((e) => place(e, f) !== 'no').length,
    unsplit: unsplitEmails.length,
    steps: [...new Set(mine.map((e) => e.step))].sort((a, b) => a - b),
  };

  // ── people: HubSpot's per-contact counts. Always split by role; never by email. ─────────
  const peopleRows = (c.people || []).filter((p) => (!f.waves.length || f.waves.includes(p.wave)) && (f.role === 'all' || p.role === f.role));
  const peopleKeys = ['contacts', 'reached', 'opened', 'clicked', 'bounced', 'optedOut', 'opened1', 'opened2', 'opened3', 'openedMixed', 'clicked2'];
  const people = peopleRows.reduce((into, p) => {
    add(into, p, peopleKeys);
    Object.keys(p).filter((k) => /^(firstOpen|lastOpen|firstClick)/.test(k)).forEach((k) => { into[k] = (into[k] || 0) + p[k]; });
    return into;
  }, zero(peopleKeys));
  people.byEmail = f.step !== 'all'; // the page says these are for all the emails together

  // ── visits ─────────────────────────────────────────────────────────────────────────────
  const visitRows = (c.visits || []).filter((v) => v.series === 'outreach');
  const bands = Object.fromEntries(BANDS.map((b) => [b, zero(VISIT_MEASURES)]));
  const kinds = zero(MACHINE_KINDS);
  const byLink = { go: zero(BANDS), direct: zero(BANDS) };
  let unsplitVisits = 0;
  let team = 0;
  visitRows.forEach((v) => {
    const where = place(v, f);
    if (where === 'no') return;
    if (v.band === 'team') { if (where === 'yes') team += v.n || 0; return; }
    if (unsplit(where)) { unsplitVisits += v.n || 0; left[where === 'role' ? 'visitsByRole' : 'visitsByWave'] += v.n || 0; return; }
    if (!bands[v.band]) return;
    add(bands[v.band], v, VISIT_MEASURES);
    if (v.band === 'machine' && kinds[v.kind] != null) kinds[v.kind] += v.n || 0;
    if (byLink[v.link]) byLink[v.link][v.band] += v.n || 0;
  });
  const visits = {
    ...bands,
    kinds,
    byLink,
    team,
    total: BANDS.reduce((a, b) => a + bands[b].n, 0),
    unsplit: unsplitVisits,
    notPlaced: (c.visits || []).filter((v) => v.series !== 'outreach').reduce((a, v) => a + (v.n || 0), 0),
  };

  // ── minutes from the send to the visit ──────────────────────────────────────────────────
  const timing = Object.fromEntries(BANDS.map((b) => [b, zero(LAG_BUCKETS)]));
  (c.visitTiming || []).forEach((t) => {
    if (t.series !== 'outreach' || place(t, f) !== 'yes' || !timing[t.band] || timing[t.band][t.bucket] == null) return;
    timing[t.band][t.bucket] += t.n || 0;
  });

  // ── pages people saw ────────────────────────────────────────────────────────────────────
  const pageMap = new Map();
  (c.visitPages || []).forEach((p) => {
    if (p.series !== 'outreach' || place(p, f) !== 'yes') return;
    if (!pageMap.has(p.page)) pageMap.set(p.page, { page: p.page, person: 0, unsure: 0, landed: 0 });
    const row = pageMap.get(p.page);
    row[p.band] += p.n || 0;
    row.landed += p.landed || 0;
  });
  const pages = [...pageMap.values()].sort((a, b) => b.person - a.person || b.unsure - a.unsure || a.page.localeCompare(b.page));

  // ── the go link's own count ─────────────────────────────────────────────────────────────
  const goHits = (c.goHits || []).filter((g) => place(g, f) === 'yes').reduce((into, g) => add(into, g, ['n', 'team', 'datacentre', 'flaggedNotHuman']), zero(['n', 'team', 'datacentre', 'flaggedNotHuman']));

  // ── each recipient's own events, where they were read: exact by wave, role and email ────
  const recipients = recipientsOf(c, f);
  // The headline figures come from the events when there are any, and from HubSpot's totals
  // when there are not. `source` says which, and the page says it too.
  const figures = recipients ? {
    source: 'events',
    delivered: recipients.sends.delivered, opened: recipients.sends.opened, read: recipients.sends.read, clicked: recipients.sends.clicked,
    people: recipients.people.people, peopleOpened: recipients.people.opened, peopleRead: recipients.people.read, peopleClicked: recipients.people.clicked,
  } : {
    source: 'hubspot',
    delivered: sent.delivered, opened: sent.opened, read: null, clicked: sent.clicked,
    people: people.reached, peopleOpened: people.opened, peopleRead: null, peopleClicked: people.clicked,
  };

  return {
    filters: f, sent, people, visits, timing, pages, goHits, recipients, figures, left,
    readers: readersOf(c), // the whole campaign, whatever is chosen: see readersOf
    roleUnsplit: f.role !== 'all' && (sent.unsplit > 0 || unsplitVisits > 0),
    // What had to be left out because its source is not divided that way. With the events
    // read, opens and clicks ARE divided, so only visits (and HubSpot's own totals) are left out.
    unsplit: left.visitsByRole + left.visitsByWave > 0 || (!recipients && left.emailsByRole + left.emailsByWave > 0),
  };
}

/**
 * Each recipient's own events, added up for the filters: what happened to each delivery, what
 * each PERSON did across their emails, and which of the emails they opened. Null when the
 * events were not read on the build.
 */
export function recipientsOf(cube, filters) {
  const r = cube && cube.recipients;
  if (!r) return null;
  const f = normaliseFilters(filters);
  const sends = r.sends.filter((row) => exact(row, f)).reduce((into, row) => add(into, row, SEND_KEYS), zero(SEND_KEYS));
  const personRows = r.persons.filter((row) => exact(row, f));
  const people = personRows.reduce((into, row) => {
    add(into, row, PERSON_KEYS);
    Object.keys(row).filter((k) => /^firstOpen\d+$/.test(k)).forEach((k) => { into[k] = (into[k] || 0) + (row[k] || 0); });
    return into;
  }, zero(PERSON_KEYS));
  const patterns = { opened: new Map(), clicked: new Map() };
  r.series.filter((row) => exact(row, f)).forEach((row) => patterns[row.what].set(row.pattern, (patterns[row.what].get(row.pattern) || 0) + (row.n || 0)));
  const listOf = (map) => [...map.entries()].map(([pattern, n]) => ({ pattern, n })).sort((a, b) => b.n - a.n || a.pattern.localeCompare(b.pattern));
  // A recipient who is no longer a contact has no role, and may have no wave: with a role or a
  // wave chosen their deliveries cannot be placed, and are counted here as left out.
  const stepOk = (row) => f.step === 'all' || row.step === f.step;
  const notPlaced = r.sends.filter((row) => stepOk(row) && !exact(row, f) && ((f.role !== 'all' && row.role === 'unknown') || (f.waves.length && row.wave == null)))
    .reduce((a, row) => a + (row.delivered || 0), 0);
  return {
    sends, people,
    patterns: { opened: listOf(patterns.opened), clicked: listOf(patterns.clicked) },
    wider: widerOf(sends, r.unsubs ? r.unsubs.filter((row) => exact(row, f)) : null),
    // HubSpot's own count of unsubscribes, for every email the choice touches (a total per
    // email: it cannot be divided by role, so it is only ever a check on the events, not a figure)
    hubspotUnsubscribed: sumNullable(((cube && cube.emails) || []).filter((e) => e.series === 'outreach' && wavesOfRow(e).length && e.step != null && place(e, f) !== 'no'), 'unsubscribed'),
    notPlaced,
    byPerson: f.step !== 'all', // `people` is for all the emails together, whatever email is chosen
    events: r.events, cut: r.cut, unknownRecipients: r.unknownRecipients, hours: r.hours,
  };
}

/**
 * The spam-complaint rate mailbox providers publish as the line for bulk senders (Google and
 * Yahoo, since February 2024: stay under 0.1% of delivered mail).
 */
export const SPAM_LIMIT = 0.001;
/** Fewer accepted than this, and emails are being refused on a scale worth looking at. */
export const DELIVERED_FLOOR = 0.95;
/** An email younger than this may still be being delivered: its rates are left out until then. */
export const SETTLED_HOURS = 24;

/**
 * ARE THE EMAILS ARRIVING? (Lee, 7 Oct 2026: ten proven visits out of thousands "is worryingly
 * low and would make us concerned that these emails aren't getting through".)
 *
 * Ten is not how many arrived. It is how many clicked through AND can be proved to be people.
 * This puts beside it everything that bears on arrival:
 *   · HubSpot's own figures: sent, accepted by the receiving mail systems, bounced, reported as
 *     spam, unsubscribed, replied. Only for emails sent more than a day ago: a newer one is
 *     still being delivered, and would read as "refused" (the first real figures did exactly
 *     that for an email sent minutes before);
 *   · bounces by email of the series: a first email to a new list finds the dead addresses;
 *   · from each recipient's events, where they were read: for how many people something at
 *     their end fetched a picture at least once (their firm's mail security or their own mail
 *     program: either way the email reached them); and the signs a PERSON saw it.
 * ⚠️ What it cannot say is inbox or junk folder: no sender is told. The page says so.
 * `verdict` is "fine" only when nothing here points to refusal or complaint; it is never
 * "the emails are being read".
 */
export function arrivalOf(cube, filters) {
  const f = normaliseFilters(filters);
  const c = cube || {};
  const builtMs = Date.parse(c.builtAt || '') || null;
  const settledBefore = builtMs ? builtMs - SETTLED_HOURS * 3600000 : null;
  const isSettled = (e) => !settledBefore || !e.sentAt || Date.parse(e.sentAt) <= settledBefore;
  const outreach = (c.emails || []).filter((e) => e.series === 'outreach' && wavesOfRow(e).length && e.step != null);
  const mine = outreach.filter((e) => place(e, f) === 'yes');
  const settled = mine.filter(isSettled);
  const total = (rows, key) => sumNullable(rows, key);
  const sent = total(settled, 'sent');
  const delivered = total(settled, 'delivered');
  const bounced = total(settled, 'bounced');
  const spam = total(settled, 'spam');
  const rate = (k, n) => (k != null && n ? k / n : null);
  // by email of the series, whatever email is chosen: the fall after the first is the point
  const anyStep = outreach.filter((e) => place(e, { ...f, step: 'all' }) === 'yes' && isSettled(e));
  const bounceByStep = [...new Set(anyStep.map((e) => e.step))].sort((a, b) => a - b).map((step) => {
    const rows = anyStep.filter((e) => e.step === step);
    return { step, sent: total(rows, 'sent'), bounced: total(rows, 'bounced'), rate: rate(total(rows, 'bounced'), total(rows, 'sent')) };
  }).filter((x) => x.rate != null);

  const r = recipientsOf(c, f);
  const out = {
    emails: settled.length,
    fresh: mine.length - settled.length,
    leftOut: outreach.filter((e) => unsplit(place(e, f))).length,
    sent, delivered, bounced, spam,
    unsubscribed: total(settled, 'unsubscribed'),
    replied: total(settled, 'replied'),
    deliveredRate: rate(delivered, sent),
    bounceRate: rate(bounced, sent),
    spamRate: rate(spam, delivered),
    bounceByStep,
    // from each recipient's own events (exact for the choice), or null where they were not read
    reached: r ? { n: r.people.openedAny, of: r.people.people } : null,
    keptOpen: r ? FATES.slice(0, 4).reduce((a, k) => a + r.sends[k], 0) : null,
    clicked: r ? r.sends.clicked : null,
    linkUnsubs: r && r.wider.link ? r.wider.link.total : null,
  };
  const reasons = [];
  if (out.deliveredRate != null && out.deliveredRate < DELIVERED_FLOOR) reasons.push('refused');
  if (out.spamRate != null && out.spamRate > SPAM_LIMIT) reasons.push('spam');
  out.verdict = !sent ? 'none' : reasons.length ? 'look' : 'fine';
  out.reasons = reasons;
  return out;
}

/**
 * Has the estimate been tried where the answer is known? Not yet. The test is a SEED TEST: one
 * of these emails sent to twenty or thirty mailboxes we control (Outlook with pictures off and
 * on, Gmail, Apple Mail), with a note of who read and who clicked, and the estimator run on
 * those alone. Until this is true the page says "untested" on every estimate, and no estimate
 * is drawn at a confidence above "low".
 */
export const SEED_TESTED = false;
/**
 * People in one firm share one mail policy, so 112 people are fewer than 112 independent
 * readings of "does this mailbox show pictures". When the range is drawn the base is counted
 * as this many times smaller than it is. It widens the range; it does not move the middle.
 *
 * The build now MEASURES one part of that (`base.firms.design`: how far members of one firm
 * agree). ⚠️ UNTIL A SEED TEST PASSES THE WIDER OF THE TWO IS USED, and the measured figure is
 * shown beside it. With mostly one unsubscriber a firm the measured figure comes out near 1,
 * and that is true of the base, but it measures one source of error only. This halving has also
 * been standing in for what is not measured: the assumption about quiet readers, scanners
 * naming themselves as Outlook, and the same clustering in the COUNT of opens. Narrowing the
 * range of an untested estimate because one of several unknowns measured small would claim
 * more than is known (the independent review of the method, 7 Oct 2026).
 */
export const BASE_ALLOWANCE = 2;
/** The allowance in force: see BASE_ALLOWANCE. `measured` is base.firms.design, or null. */
export const allowanceFor = (measured, seedTested = SEED_TESTED) => {
  const m = Number.isFinite(measured) && measured >= 1 ? measured : null;
  if (seedTested) return m == null ? BASE_ALLOWANCE : m;
  return Math.max(BASE_ALLOWANCE, m == null ? 0 : m);
};
/** Fewer people than this in the base, or fewer of them with an open, and no estimate is made. */
export const MIN_ESTIMATE_BASE = 30;
export const MIN_ESTIMATE_SEEN = 3;
/** More than this share of delivered emails "probably read" is hard to believe of a cold email. */
export const IMPLAUSIBLE_SHARE = 0.5;
/**
 * Fewer counted opens than this for a choice, and NO estimate is made for it. Scaling 0 gives
 * "0% to 0%" and scaling 1 gives "0% to 5%": both say something exact about almost nothing. (A
 * count from this up to SMALL_BASE is estimated, at "very low" confidence.)
 */
export const MIN_ESTIMATE_COUNT = 10;
/**
 * THE CHECKS ON THE ESTIMATE. Their pass lines were fixed on 7 Oct 2026, before any of the
 * figures they use existed, and are not to be moved after seeing them:
 *   timing       fails if both sides hold MIN_CHECK or more and their ranges do not overlap
 *   definitions  fails if the middle under either other definition of an open falls outside
 *                the main estimate's own range
 * Under MIN_CHECK on a side is "too few to tell", never a pass.
 * ⚠️ BOTH ARE WEAK and the page says so: two ranges on 30 and 80 people fail to overlap only
 * when the shares are far apart, and the definitions are compared against a range that runs
 * from about 20% to 75%. So a pass reads "no large difference found", never as support.
 */
export const DEFINITIONS = [
  { key: 'program', count: 'program', seen: 'seenProgram', label: 'a person’s own mail program' },
  { key: 'programOrApple', count: 'programOrApple', seen: 'seenProgramOrApple', label: 'a mail program, or Apple’s mail service' },
  { key: 'keptOpen', count: 'keptOpen', seen: 'seenKeptOpen', label: 'any open HubSpot kept' },
];
const ranged = (k, n) => { const w = wilson(k, n); return w ? { k, n, rate: k / n, low: w.low, high: w.high } : { k, n, rate: null, low: null, high: null }; };
/**
 * One split by timing: those who acted within ten minutes of the email arriving, and later.
 * @returns null when the split was not stored; else { early, later, untimed, verdict } with
 *          verdict 'few' | 'none-found' | 'differs'
 */
export function timingCheck(parts) {
  if (!parts || !parts.early || !parts.later) return null;
  const early = ranged(parts.early.seen || 0, parts.early.n || 0);
  const later = ranged(parts.later.seen || 0, parts.later.n || 0);
  const verdict = early.n < MIN_CHECK || later.n < MIN_CHECK ? 'few' : early.low <= later.high && later.low <= early.high ? 'none-found' : 'differs';
  return { early, later, untimed: parts.untimed || 0, verdict };
}

/**
 * An estimate in words: its RANGE, rounded outwards to the nearest 5%. The one place that words
 * it, so the headline, the charts and the table cannot differ. ⚠️ An estimate is never printed
 * as its middle alone, and never as "k of n": that reads as a count.
 */
const to5 = (x, way) => `${Math[way]((x * 100) / 5) * 5}%`;
export const estimateText = (share) => `${to5(share.low, 'floor')} to ${to5(share.high, 'ceil')}`;
/**
 * The middle in words ("40%"), or null when it is over IMPLAUSIBLE_SHARE. A "most likely" figure
 * that is itself hard to believe is not offered as one: a range of 55% to 100% once printed
 * "about 100%" beside the banner saying it was hard to believe.
 */
export const estimateMiddle = (share) => (share.mid > IMPLAUSIBLE_SHARE ? null : to5(share.mid, 'round'));
/**
 * A share rounded to the nearest 5%, for setting the three definitions' middles SIDE BY SIDE
 * in the checks (and only there: a middle is never the headline, and never a count).
 */
export const estimateAbout = (x) => (Number.isFinite(x) ? to5(x, 'round') : null);

/** k of n, with the base counted as `allowance` times smaller for the range. */
const allowed = (k, n, allowance = BASE_ALLOWANCE) => {
  const w = wilson(k / allowance, n / allowance);
  return w ? { k, n, rate: k / n, low: w.low, high: w.high } : null;
};
const overlap = (a, b) => Boolean(a && b) && a.low <= b.high && b.low <= a.high;

/**
 * HOW MANY PROBABLY READ IT: AN ESTIMATE, NOT A COUNT.
 *
 * The count: emails where a PERSON'S OWN MAIL PROGRAM loaded a picture (`sends.program`). That
 * leaves out, on purpose, every open HubSpot marked as a machine, every open made by a click,
 * Apple's background fetches, and "browser name" opens (HubSpot had kept one before 100 of 253
 * clicks known to be machines). So the count is corrected DOWN before anything else.
 *
 * The scaling: of the people who demonstrably had one of these emails open, because they
 * unsubscribed using the link inside it, what share had such an open recorded? That share is
 * how much of the readership a mail-program open sees; dividing by it corrects UP for readers
 * with pictures off. ONE definition of an open on both sides.
 *   base    the link unsubscribers, for the WHOLE CAMPAIGN whatever is chosen: it is a fact
 *           about how these recipients' mailboxes behave, and too small to cut by wave.
 *           "Seen" is a mail-program open of that email AT ANY TIME, as the count is (until
 *           version 3 of the figures it was "at least two seconds before they unsubscribed":
 *           the two sides did not match. ⚠️ Changed on 7 Oct 2026 AFTER the first figures were
 *           seen, on grounds of consistency; it moves the estimate DOWN a little, 17 of 112
 *           becoming 18 or 19)
 *   second  the clicks HubSpot kept, read the same way. Not all of those are people, so it is
 *           a second reading, not the base. If the two part company, confidence drops.
 *   range   from the base counted as `allowance` times smaller than it is: the wider of
 *           the halving and what the build measured by firm (allowanceFor)
 *
 * WHAT IT ASSUMES, and the page says: that people who unsubscribe show pictures about as often
 * as people who read quietly. That cannot be checked from these figures.
 * WHAT IT CANNOT DO: say which wave or email did better. One rate for the whole campaign is
 * applied to each, so the order is the order of the counted opens.
 *
 * `flags` are the checks that lower `confidence` from "low" to "very-low":
 *   implausible  the middle of the estimate is over half of everything delivered
 *   basesPart    the two readings of the scaling do not overlap
 *   thin         it rests on fewer than SMALL_BASE counted opens for this choice
 *   timingDiffers / clickTimingDiffers / definitionSwings   one of the checks above failed
 *   firmsClustered  measured by firm, the base is further from separate readings than the
 *                halving allowed for (the measured figure is over BASE_ALLOWANCE): one or a few
 *                firms hold much of it. The range is then drawn from the measured figure, and
 *                can run "10% to 100%"; that is not a "low"-confidence estimate, it is "very low"
 * and `untested` is true until SEED_TESTED is. Nothing here is ever "high".
 *
 * @returns null when each recipient's events were not read; { ok: false, why } when there is no
 *          estimate: no base to scale from ('not-read', 'thin-base'), too few counted opens for
 *          this choice ('thin-count'), or a scaling that comes to more than was delivered
 *          ('over'); otherwise the estimate for the filters.
 */
export function estimatesOf(cube, filters) {
  const r = cube && cube.recipients;
  if (!r) return null;
  const f = normaliseFilters(filters);
  const b = r.base;
  if (!b) return { ok: false, why: 'not-read' };
  const baseN = b.n || 0;
  const baseSeen = b.seenProgram || 0;
  if (baseN < MIN_ESTIMATE_BASE || baseSeen < MIN_ESTIMATE_SEEN) return { ok: false, why: 'thin-base', baseN, baseSeen };
  const measured = b.firms && Number.isFinite(b.firms.design) ? b.firms.design : null;
  const allowance = allowanceFor(measured);
  const base = allowed(baseSeen, baseN, allowance);
  const all = r.sends.reduce((into, row) => add(into, row, ['clicked', 'clickProgram']), zero(['clicked', 'clickProgram']));
  const second = all.clicked >= MIN_ESTIMATE_BASE ? allowed(all.clickProgram, all.clicked, allowance) : null;

  const COUNTS = ['delivered', ...DEFINITIONS.map((d) => d.count)];
  const mine = r.sends.filter((row) => exact(row, f)).reduce((into, row) => add(into, row, COUNTS), zero(COUNTS));
  // THE EDGES. Too few counted opens: nothing to scale. And when the middle of the scaling
  // comes to everything delivered or more, the scaling does not fit this choice: no estimate,
  // never "100% to 100%".
  if (mine.program < MIN_ESTIMATE_COUNT) return { ok: false, why: 'thin-count', counted: mine.program, delivered: mine.delivered };
  if (mine.program / base.rate >= mine.delivered) return { ok: false, why: 'over', counted: mine.program, delivered: mine.delivered };
  const cap = (x) => Math.min(x, mine.delivered);
  const emails = { low: cap(mine.program / base.high), mid: cap(mine.program / base.rate), high: cap(mine.program / base.low) };
  const share = mine.delivered ? { low: emails.low / mine.delivered, mid: emails.mid / mine.delivered, high: emails.high / mine.delivered } : null;

  // The checks (pass lines fixed in advance: see DEFINITIONS above).
  const timing = timingCheck(b.timing);
  const clickTiming = timingCheck(r.clickTiming);
  const middles = DEFINITIONS.map((d) => {
    const seen = b[d.seen] || 0;
    const counted = mine[d.count] || 0;
    const mid = seen > 0 && mine.delivered ? cap(counted / (seen / baseN)) / mine.delivered : null;
    return { key: d.key, label: d.label, counted, seen, mid };
  });
  // A definition whose two figures are the SAME as the narrow one's is not a second reading. On
  // the real figures HubSpot keeps no open from Apple's mail service, so "a mail program, or
  // Apple's" IS the narrow definition (third dry run, 7 Oct 2026: 1,460 counted and 20 of 116
  // seen under both). It is shown as a copy and is not counted as a check: a copy that agrees
  // with its original is not evidence.
  middles.slice(1).forEach((m) => { m.same = m.counted === middles[0].counted && m.seen === middles[0].seen; });
  const others = middles.slice(1).filter((m) => !m.same);
  const definitions = {
    middles,
    // how many OTHER readings there really are (0, 1 or 2)
    readings: others.length,
    verdict: !share || !others.length || others.some((m) => m.mid == null) ? 'cannot' : others.some((m) => m.mid < share.low || m.mid > share.high) ? 'differs' : 'none-found',
  };

  const flags = [];
  if (share && share.mid > IMPLAUSIBLE_SHARE) flags.push('implausible');
  if (second && !overlap(base, second)) flags.push('basesPart');
  if (mine.program < SMALL_BASE) flags.push('thin');
  if (timing && timing.verdict === 'differs') flags.push('timingDiffers');
  if (clickTiming && clickTiming.verdict === 'differs') flags.push('clickTimingDiffers');
  if (definitions.verdict === 'differs') flags.push('definitionSwings');
  if (measured != null && measured > BASE_ALLOWANCE) flags.push('firmsClustered');
  return {
    ok: true,
    counted: mine.program,
    delivered: mine.delivered,
    emails, share,
    base, second,
    // one counted open stands for this many readers: said in the guide, as the scaling it is
    scale: { low: 1 / base.high, mid: 1 / base.rate, high: 1 / base.low },
    flags,
    untested: !SEED_TESTED,
    confidence: flags.length ? 'very-low' : 'low',
    // the allowance in force, and what was measured by firm beside it (null: not worked out)
    allowance,
    measured: b.firms ? { groups: b.firms.groups || 0, design: measured } : null,
    // our own addresses that unsubscribed by the link: left out of the base, and said
    ownLeftOut: b.ownLeftOut || 0,
    checks: { timing, clickTiming, definitions },
  };
}

/**
 * AT LEAST, ON THE RECORD: emails that were in front of a person, as a COUNT and not an
 * estimate. A person's own mail program loaded a picture (and not within two seconds of a
 * click), or they unsubscribed by the link inside the email. "On the record", not "proven":
 * the first part rests on reading a program's name.
 *
 * BESIDE it, never in it: deliveries with neither sign and a click HubSpot kept (`alsoClicked`).
 * A kept click is not proof of a person; the reader may add them, and the page says what that
 * adds. ⚠️ Never added to the estimate, and never shown in the estimate's colour.
 *
 * @returns null when each recipient's events were not read; else the figures for the filters.
 *          `people` is null when an email of the series is chosen (a person is counted across
 *          all their emails).
 */
export function floorOf(cube, filters) {
  const r = cube && cube.recipients;
  if (!r) return null;
  const f = normaliseFilters(filters);
  const KEYS = ['delivered', 'shownProgram', 'shownUnsub', 'alsoClicked', 'shownNoDelivery', 'ownLeftOut'];
  const mine = r.sends.filter((row) => exact(row, f)).reduce((into, row) => add(into, row, KEYS), zero(KEYS));
  const floor = mine.shownProgram + mine.shownUnsub;
  const PEOPLE = ['people', 'shown', 'alsoClicked'];
  const persons = f.step === 'all' ? r.persons.filter((row) => exact(row, f)).reduce((into, row) => add(into, row, PEOPLE), zero(PEOPLE)) : null;
  return {
    delivered: mine.delivered,
    floor,
    program: mine.shownProgram,
    unsub: mine.shownUnsub,
    share: ranged(floor, mine.delivered),
    alsoClicked: mine.alsoClicked,
    withClicks: ranged(floor + mine.alsoClicked, mine.delivered),
    noDelivery: mine.shownNoDelivery,
    // emails to our own addresses with such a sign: in neither the floor nor what is beside it
    ownLeftOut: mine.ownLeftOut,
    // WHY a kept click is beside the floor and not in it, in the campaign's own figures (they do
    // not follow the choice): how many of the clicks HubSpot kept came within ten minutes of
    // the email arriving, and how many of those had no mail-program open before the click.
    // That is what a scanner does. null when the split was not stored.
    keptClicks: r.clickTiming ? {
      total: r.clickTiming.early.n + r.clickTiming.later.n + r.clickTiming.untimed,
      early: r.clickTiming.early.n,
      earlyNoOpen: r.clickTiming.early.n - r.clickTiming.early.seen,
    } : null,
    // who unsubscribed was not read: the floor is then the mail-program part alone
    unsubsRead: Boolean(r.base),
    people: persons ? { people: persons.people, shown: persons.shown, alsoClicked: persons.alsoClicked, share: ranged(persons.shown, persons.people) } : null,
  };
}

/**
 * The estimate for each wave, each role, or each email of the series, as rows for the range
 * chart. ⚠️ The same scaling is applied to every row, so the ORDER is that of the counted opens:
 * this shows how wide each estimate is, not which group did better.
 */
export function estimateRows(cube, filters, dimension) {
  const f = normaliseFilters(filters);
  let values = [];
  if (dimension === 'wave') values = wavesOf(cube).filter((w) => w.sent && (!f.waves.length || f.waves.includes(w.wave))).map((w) => ({ key: String(w.wave), label: `Wave ${w.wave}`, with: { waves: [w.wave] } }));
  else if (dimension === 'role') values = ['head', 'other'].map((role) => ({ key: role, label: ROLE_LABEL[role], with: { role } }));
  else values = stepsOf(cube).map((step) => ({ key: String(step), label: `Email ${step}`, with: { step } }));
  return values.map((v) => {
    const e = estimatesOf(cube, { ...f, ...v.with });
    if (!e || !e.delivered) return null;
    // ⚠️ No `k` and no `n` in a row: the chart is given a range to print, never a count.
    if (!e.ok) return { key: v.key, label: v.label, rate: null, none: e.why === 'thin-count' ? 'too few counted opens to estimate' : 'no estimate', counted: e.counted };
    return { key: v.key, label: v.label, rate: e.share.mid, low: e.share.low, high: e.share.high, figure: estimateText(e.share), small: e.flags.length > 0, counted: e.counted };
  }).filter(Boolean);
}

/**
 * A WIDER BASE THAN THE VISITS WE CAN PROVE WERE PEOPLE (Lee, 7 Oct 2026: "why are we working
 * from just 10 people … surely we have a lot more than 10"). Ten is everyone who clicked
 * through and then did on our site what only a person does. Two larger groups also did
 * something that needs no picture, on HubSpot's word alone that they were people:
 *   clicked     every email HubSpot counts as clicked (its own filter for machines)
 *   link        unsubscribed by the link in the email, so had it open
 *   button      unsubscribed with their mail program's own button, which can be done without
 *               opening the email: "no open" there is not "read with pictures off"
 * For each: had an open been recorded?
 *   before     at least two seconds before
 *   atMoment   only at that moment (for a click, most likely the click itself)
 *   later      only afterwards
 *   none       no open at all, or only a machine's
 * THE FIGURE TO READ is the same one the visits lead with: `notBefore`, no open recorded before
 * they did it, beside `openBefore`. What counts as "before" differs, and must:
 *   a click        an open only AT the click is most likely the click itself (HubSpot counts a
 *                  click as an open), so it is NOT before: notBefore = atMoment + later + none
 *   an unsubscribe makes no open of its own, and someone with pictures on opens the email and
 *                  unsubscribes within the minute, so an open at that moment IS before:
 *                  notBefore = later + none
 * `unsubs` is null when who unsubscribed was not read: then `link` and `button` are null too,
 * and the page says "not read", never 0.
 */
function widerOf(sends, unsubRows) {
  const group = (get, isClick) => {
    const before = get('program') + get('apple') + get('browser');
    const atMoment = get('atClick');
    const later = get('after');
    const none = get('machine') + get('none');
    const openBefore = isClick ? before : before + atMoment;
    const total = before + atMoment + later + none;
    return { before, atMoment, later, none, openBefore, notBefore: total - openBefore, total };
  };
  const cap = (w) => w.charAt(0).toUpperCase() + w.slice(1);
  const clicked = group((o) => sends[`click${cap(o)}`] || 0, true);
  const how = (h) => (unsubRows ? group((o) => unsubRows.filter((u) => u.how === h && u.outcome === o).reduce((a, u) => a + u.n, 0), false) : null);
  return { clicked, link: how('link'), button: how('button'), other: how('other'), unsubsRead: Boolean(unsubRows) };
}

/**
 * READERS THE OPEN COUNT CANNOT SEE.
 *
 * Someone who reads an email with its pictures off is not counted as an open by anyone. The
 * only thing that shows them is a click. So: of the visits we know were people, in how many had
 * the email loaded a picture before the click? (functions/outreach/readers.js makes the match.)
 *
 *   seen       a picture had loaded at least two seconds before the click: by a mail program,
 *              Apple's service, or something calling itself a browser. ⚠️ Not proof of a person:
 *              before 100 of 253 machine clicks a "browser name" open had been KEPT by HubSpot
 *   atOrAfter  the only opens HubSpot kept are within two seconds of the click (`atClick`: it
 *              counts a click as an open, so most likely the click itself) or later (`after`)
 *   unseen     no open at all, or only a machine's
 *   notBefore  atOrAfter + unseen: no open recorded before the click. THE COUNT THE PAGE LEADS
 *              WITH. An open that was nobody can only make it too small, never too large.
 *
 * ⚠️ THE WHOLE CAMPAIGN, WHATEVER FILTERS ARE CHOSEN. The figures are stored as totals by kind
 * of visitor and outcome and nothing else: a row per email would mostly be a count of 1, and
 * that is enough to work out who it was for someone who can also see HubSpot. So this takes no
 * filters, and the check below is made ONCE (made per email it would have many chances to pass
 * by luck).
 *
 * A COUNT IS ALWAYS GIVEN; A SHARE OF READERS ONLY WHEN ALL FOUR HOLD (`statement.ok`):
 *   (a) at least MIN_READERS visits by people were matched;
 *   (b) at least MIN_CHECK visits by machines were;
 *   (c) for machines, "a person's mail program had loaded a picture before the click" is at
 *       most CHECK_CEILING: a scanner clicks before anyone could have read the email;
 *   (d) and it is clearly rarer for machines than for people (the ranges do not touch).
 * ⚠️ THIS NEVER SCALES HUBSPOT'S OPEN COUNT UP. "For every 10 opens, N people read it" corrects
 * upward for the readers the count missed and not downward for the opens that were nobody: the
 * first version of this said it, and the independent review of PR #59 caught it. The estimate
 * that corrects both ways is `estimatesOf`, and it is kept apart from these counts.
 * ⚠️ (d) cannot pass while most proven readers have pictures off, which is what the first real
 * figures show (1 of 10 by a mail program). The conditions were NOT changed after seeing that:
 * a gate redrawn round the data it is meant to judge is no gate.
 */
export function readersOf(cube) {
  const r = cube && cube.readers;
  if (!r) return null;
  const bands = Object.fromEntries(BANDS.map((b) => [b, zero(READER_OUTCOMES)]));
  r.rows.forEach((row) => { bands[row.band][row.outcome] += row.n; });
  BANDS.forEach((b) => {
    const x = bands[b];
    x.seen = x.program + x.apple + x.browser;
    x.atOrAfter = x.atClick + x.after;
    x.unseen = x.machine + x.none;
    x.notBefore = x.atOrAfter + x.unseen;
    x.matched = x.seen + x.notBefore;
    x.visits = x.matched + x.ambiguous + x.unmatched;
  });
  const p = bands.person;
  const m = bands.machine;
  const people = wilson(p.program, p.matched); // people: a person's own mail program before the click
  const control = wilson(m.program, m.matched); // machines, on the same measure: the check

  let why = null;
  if (p.matched < MIN_READERS) why = 'few';
  else if (m.matched < MIN_CHECK) why = 'no-check';
  else if (control.rate > CHECK_CEILING) why = 'check-high';
  else if (!(control.high < people.low)) why = 'check-failed';
  // The share of PROVEN readers with no open before their click, with the range so few allow.
  const statement = why ? { ok: false, why } : { ok: true, missed: wilson(p.notBefore, p.matched) };

  const gaps = Object.fromEntries(BANDS.map((b) => [b, zero(READER_GAPS)]));
  r.gaps.forEach((g) => { gaps[g.band][g.bucket] += g.n; });
  // When the people came: a visit within minutes of a send sits among the scanners' clicks.
  const timing = ((cube && cube.visitTiming) || []).filter((t) => t.series === 'outreach' && t.band === 'person');
  const soon = {
    n: timing.filter((t) => t.bucket === '0-2m' || t.bucket === '2-10m').reduce((a, t) => a + (t.n || 0), 0),
    of: timing.reduce((a, t) => a + (t.n || 0), 0),
  };
  return { bands, people, control, statement, gaps, soon, clicksRead: r.clicksRead };
}

/**
 * What the headline figures are a share OF.
 *
 * With no single email chosen, the question is about PEOPLE: of those sent to, how many opened
 * at least one email. HubSpot keeps that per contact, so it splits by wave and by role for
 * every email. With one email chosen, the question is about THAT email, and HubSpot's
 * per-email figures answer it (split by role only where the email went to one role).
 */
export function basisOf(sel) {
  const { sent, figures, filters } = sel;
  const events = figures.source === 'events';
  if (filters.step === 'all') {
    return { kind: 'people', base: figures.people, opened: figures.peopleOpened, read: figures.peopleRead, clicked: figures.peopleClicked, counting: sent.counting,
      baseLabel: events ? 'People an email was delivered to' : 'People sent to', ofLabel: events ? 'people reached' : 'people sent to',
      openedLabel: 'Opened at least one email, as HubSpot records it', readLabel: 'Kept one open for 8 seconds or more',
      clickedLabel: 'Clicked in at least one, as HubSpot records it', source: figures.source };
  }
  return { kind: 'emails', base: figures.delivered, opened: figures.opened, read: figures.read, clicked: figures.clicked, counting: sent.counting,
    baseLabel: `Email ${filters.step}: delivered`, ofLabel: 'emails delivered', openedLabel: 'Opened, as HubSpot records it',
    readLabel: 'Kept open for 8 seconds or more', clickedLabel: 'Clicked, as HubSpot records it', source: figures.source };
}

/**
 * The rungs from "sent" to "a quote", each with what it is a share OF.
 * `upTo` is the figure if every "cannot tell" visit were a person: the two are drawn together.
 */
export function ladder(sel) {
  const { visits } = sel;
  const person = visits.person;
  const b = basisOf(sel);
  return [
    { key: 'base', label: b.baseLabel, value: b.base, of: null, source: 'HubSpot' },
    { key: 'opened', label: b.openedLabel, value: b.opened, of: b.base, source: 'HubSpot', caveat: 'A picture was fetched, or a link clicked. Not the same as read.' },
    // Only the recipients' own events carry how long an open lasted.
    ...(b.read == null ? [] : [{ key: 'kept', label: b.readLabel, value: b.read, of: b.base, source: 'HubSpot', caveat: 'HubSpot times how long the email stayed open. The nearest thing there is to “read”, for those with pictures on.' }]),
    { key: 'clicked', label: b.clickedLabel, value: b.clicked, of: b.base, source: 'HubSpot', caveat: 'Includes security scanners HubSpot did not recognise.' },
    { key: 'visits', label: 'Visits that reached our site', value: visits.total, of: b.base, source: 'our site', split: { person: person.n, unsure: visits.unsure.n, machine: visits.machine.n } },
    { key: 'people', label: 'Visits by people, on the evidence', value: person.n, upTo: person.n + visits.unsure.n, of: b.base, source: 'our site', caveat: 'The hatched bar adds the visits we cannot tell: the truth lies between the two.' },
    { key: 'read', label: 'Stayed a minute or more', value: person.read60, of: person.n, source: 'our site', among: 'people' },
    { key: 'second', label: 'Opened a second page', value: person.secondPage, of: person.n, source: 'our site', among: 'people' },
    { key: 'slider', label: 'Used the price slider', value: person.slider, of: person.n, source: 'our site', among: 'people' },
    { key: 'cta', label: 'Clicked a button', value: person.cta, of: person.n, source: 'our site', among: 'people' },
    { key: 'quote', label: 'Ran a quote', value: person.quote, of: person.n, source: 'our site', among: 'people' },
  ].map((r) => ({ ...r, ofLabel: b.ofLabel }));
}

/** Are there figures for each email at all (the events, or HubSpot's totals)? Without them only the per-person measures can be compared. */
export function hasEmailFigures(cube) {
  return Boolean(cube && cube.recipients) || ((cube && cube.emails) || []).some((e) => e.series === 'outreach' && e.delivered != null);
}

/**
 * What can be compared, and what each is a share of.
 *   perEmail   a figure for each email (so the waves are like for like only on one email)
 *   exact      divided by the contact's own wave and role when the events were read, so heads
 *              and others can be compared on EVERY email, and the two-wave email by wave
 *   needsEvents only there when each recipient's events were read
 */
export const METRICS = {
  opened: { label: 'Opened', long: 'Opened, as HubSpot records it', of: 'emails delivered', k: (s) => s.figures.opened, n: (s) => s.figures.delivered, perEmail: true, exact: true },
  kept: { label: 'Kept open 8 s+', long: 'Kept open for 8 seconds or more', of: 'emails delivered', k: (s) => s.figures.read, n: (s) => s.figures.delivered, perEmail: true, exact: true, needsEvents: true },
  clicked: { label: 'Clicked', long: 'Clicked, as HubSpot records it', of: 'emails delivered', k: (s) => s.figures.clicked, n: (s) => s.figures.delivered, perEmail: true, exact: true },
  // Visits carry the EMAIL's own place (a visit does not say who its visitor is), so they are
  // set against HubSpot's totals for the same emails: the two are left out together.
  visited: { label: 'Visited', long: 'Visits by people, or that we cannot tell', of: 'emails delivered', k: (s) => s.visits.person.n + s.visits.unsure.n, n: (s) => s.sent.delivered, perEmail: true },
  peopleOpened: { label: 'People who opened', long: 'People who opened at least one email', of: 'people sent to', k: (s) => s.figures.peopleOpened, n: (s) => s.figures.people, perEmail: false },
  bounced: { label: 'Bounced', long: 'Bounced', of: 'emails sent', k: (s) => s.sent.bounced, n: (s) => s.sent.sent, perEmail: true },
};
/** The measures on offer for these figures. */
export function metricsFor(cube) {
  if (!hasEmailFigures(cube)) return ['peopleOpened'];
  return ['opened', ...(cube && cube.recipients ? ['kept'] : []), 'clicked', 'visited', 'peopleOpened'];
}

const ROLE_LABEL = { head: 'Heads', other: 'Others' };

/**
 * One group against the others, by rate.
 * @param dimension 'wave' | 'role' | 'step'
 * @returns {{ rows, verdict, notes, metric }}
 *   rows: [{ key, label, k, n, rate, low, high, counting, small }]
 */
export function compare(cube, filters, dimension, metricKey) {
  const f = normaliseFilters(filters);
  const metric = METRICS[metricKey] || METRICS.opened;
  const notes = [];
  let values = [];
  if (dimension === 'wave') {
    values = wavesOf(cube).filter((w) => w.sent && (!f.waves.length || f.waves.includes(w.wave))).map((w) => ({ key: String(w.wave), label: `Wave ${w.wave}`, with: { waves: [w.wave] } }));
    if (metric.perEmail && f.step === 'all') notes.push('The waves have had different numbers of emails so far. Choose "Email 1" above to compare like with like.');
  } else if (dimension === 'role') {
    values = ['head', 'other'].map((r) => ({ key: r, label: ROLE_LABEL[r], with: { role: r } }));
    if (metric.perEmail && f.step !== 1 && !(metric.exact && cube && cube.recipients)) notes.push('Only email 1 is sent to heads and others separately, so this compares email 1. "People who opened" compares every email.');
  } else {
    values = stepsOf(cube).map((s) => ({ key: String(s), label: `Email ${s}`, with: { step: s } }));
    if (f.role !== 'all' && !(metric.exact && cube && cube.recipients)) notes.push('Emails 2 and 3 go to heads and others together, so they are left out while a role is chosen.');
    if (!metric.perEmail) notes.push('HubSpot keeps this per person, not per email, so it cannot be compared across the series.');
  }
  // A per-person figure cannot be laid across the series: it would be the same number three times.
  if (dimension === 'step' && !metric.perEmail) values = [];
  // Is this measure divided by the contact's own wave and role (the events), or by the email's?
  const divided = metric.exact && Boolean(cube && cube.recipients);
  let together = false;
  const rows = values.map((v) => {
    const s = select(cube, { ...f, ...v.with });
    if (dimension === 'wave' && metric.perEmail && !divided && s.left.emailsByWave > 0) together = true;
    const k = metric.k(s);
    const n = metric.n(s);
    const w = k == null || n == null ? null : wilson(k, n);
    return { key: v.key, label: v.label, k, n, rate: w ? w.rate : null, low: w ? w.low : null, high: w ? w.high : null, counting: s.sent.counting, small: !n || n < SMALL_BASE };
  }).filter((r) => r.n);
  if (together) notes.push('One email went to two waves together. For this measure it cannot be divided between them, so it is left out of each.');
  if (rows.some((r) => r.counting)) notes.push('A hollow marker is an email under a week old: it is still collecting opens and clicks.');
  // ⚠️ Waves that have had different numbers of emails are not like for like: an earlier wave
  // has had more emails, and longer, to be opened. No wave is said to stand apart on that basis.
  if (dimension === 'wave' && (f.step === 'all' || !metric.perEmail)) {
    const sentSoFar = wavesOf(cube).filter((w) => rows.some((r) => r.key === String(w.wave))).map((w) => w.emailsSent || 0);
    const least = Math.min(...sentSoFar);
    const most = Math.max(...sentSoFar);
    if (sentSoFar.length > 1 && least !== most) {
      const text = `Not like for like: these waves have had between ${least} and ${most} emails each, so the earlier ones have had more chances. ${metric.perEmail ? 'Choose "Email 1" above' : 'Choose "Opened" and "Email 1"'} to compare them fairly.`;
      return { rows, verdict: { apart: [], text, unfair: true }, notes: notes.filter((n) => !/different numbers of emails/.test(n)), metric };
    }
  }
  return { rows, verdict: verdictOf(rows, dimension), notes, metric };
}

const DIMENSION_WORD = { wave: 'wave', role: 'group', step: 'email' };
/** A rate in words: one decimal under 10% (9.4% and 9.9% are different), whole numbers above. */
const pc = (v) => {
  if (v == null || Number.isNaN(v)) return '—';
  const x = v * 100;
  if (x === 0) return '0%';
  return x < 10 ? `${x.toFixed(1).replace(/\.0$/, '')}%` : `${Math.round(x)}%`;
};

/**
 * Does any group stand apart from the rest? Only when its range does not touch the range of
 * all the others added together. With one group, or groups too small, there is nothing to say.
 */
export function verdictOf(rows, dimension) {
  const solid = rows.filter((r) => !r.small && r.rate != null);
  const word = DIMENSION_WORD[dimension] || 'group';
  if (solid.length < 2) return { apart: [], text: `Not enough to compare one ${word} with another yet.` };
  const apart = [];
  solid.forEach((r) => {
    const rest = solid.filter((o) => o.key !== r.key);
    const w = wilson(rest.reduce((a, o) => a + o.k, 0), rest.reduce((a, o) => a + o.n, 0));
    if (!w) return;
    if (r.low > w.high) apart.push({ key: r.key, label: r.label, way: 'higher', rate: r.rate, rest: w.rate });
    else if (r.high < w.low) apart.push({ key: r.key, label: r.label, way: 'lower', rate: r.rate, rest: w.rate });
  });
  if (!apart.length) return { apart, text: `No ${word} stands apart: the differences are within what chance alone would give.` };
  const text = apart.map((a) => `${a.label} is ${a.way} than the rest (${pc(a.rate)} against ${pc(a.rest)}), by more than chance would give.`).join(' ');
  return { apart, text };
}

/**
 * Across the series, per person: how many emails each opener opened, and which email first
 * got an open. HubSpot's per-contact counts; for all the emails a person has had.
 */
export function acrossSeries(sel) {
  if (sel.recipients) {
    // Exact: each person's opens of each email, from their own events.
    const p = sel.recipients.people;
    const first = Object.keys(p).filter((k) => /^firstOpen\d+$/.test(k)).map((k) => ({ step: Number(k.replace('firstOpen', '')), n: p[k] })).sort((a, b) => a.step - b.step);
    return {
      exact: true,
      base: p.people,
      openers: p.opened,
      howMany: [
        { key: 'one', label: 'Opened one email', value: p.opened1 },
        { key: 'two', label: 'Opened two', value: p.opened2 },
        { key: 'three', label: 'Opened three or more', value: p.opened3 },
      ],
      again: p.opened2 + p.opened3,
      firstOpen: first,
      patterns: sel.recipients.patterns.opened,
    };
  }
  const p = sel.people;
  const first = Object.keys(p).filter((k) => /^firstOpen\d+$/.test(k)).map((k) => ({ step: Number(k.replace('firstOpen', '')), n: p[k] })).sort((a, b) => a.step - b.step);
  return {
    exact: false,
    base: p.reached,
    openers: p.opened,
    howMany: [
      { key: 'one', label: 'Opened one email', value: p.opened1 },
      { key: 'two', label: 'Opened two', value: p.opened2 },
      { key: 'three', label: 'Opened three or more', value: p.opened3 },
      { key: 'mixed', label: 'Had opened something of ours before', value: p.openedMixed },
    ],
    again: p.opened2 + p.opened3,
    firstOpen: first,
    patterns: [],
  };
}

/** "13" → "emails 1 and 3"; "2" → "email 2 only". */
export function patternLabel(pattern) {
  const steps = String(pattern || '').split('');
  if (steps.length === 1) return `Email ${steps[0]} only`;
  return `Emails ${steps.slice(0, -1).join(', ')} and ${steps[steps.length - 1]}`;
}

/** Our own count of picture fetches, added up, and by day. */
export function picturesOf(cube) {
  const p = (cube && cube.pictures) || { days: [], programs: {}, hours: [] };
  const totals = p.days.reduce((into, d) => add(into, d, ['program', 'apple', 'scanner', 'preview']), zero(['program', 'apple', 'scanner', 'preview']));
  return { ...p, totals, total: totals.program + totals.apple + totals.scanner };
}

/**
 * Every email, with the visits that came from it. A visit is matched to its email by wave,
 * place in the series and role, which between them name one email.
 */
export function emailRows(cube, { otherSends = false } = {}) {
  const c = cube || {};
  const waveKey = (row) => wavesOfRow(row).join('+');
  const visitsFor = (e) => {
    const out = zero(BANDS);
    (c.visits || []).forEach((v) => { if (v.series === 'outreach' && waveKey(v) === waveKey(e) && v.step === e.step && v.role === e.role && out[v.band] != null) out[v.band] += v.n || 0; });
    return out;
  };
  return (c.emails || []).filter((e) => (otherSends ? e.series !== 'outreach' : e.series === 'outreach'))
    .map((e) => {
      const waves = wavesOfRow(e);
      return { ...e, waves, visits: e.series === 'outreach' && waves.length ? visitsFor(e) : null, placed: waves.length > 0 && e.step != null };
    });
}

/** Is an email in the table for these filters? Shown if any of its waves is chosen; unplaced ones always. */
export function emailShown(e, filters) {
  const f = normaliseFilters(filters);
  const waves = wavesOfRow(e);
  if (f.waves.length && waves.length && !waves.some((w) => f.waves.includes(w))) return false;
  if (f.step !== 'all' && e.step !== f.step) return false;
  return f.role === 'all' || e.role === f.role || e.role === 'both' || e.role == null;
}

/** How fresh the figures are, in words. */
export function freshness(status, now = Date.now()) {
  const s = status || {};
  if (!s.builtAt) return { text: s.running ? 'The first figures are being built now…' : 'No figures have been built yet.', stale: true };
  const hours = (now - Date.parse(s.builtAt)) / 3600000;
  const when = hours < 1 ? 'in the last hour' : hours < 24 ? `${Math.round(hours)} hours ago` : hours < 48 ? 'yesterday' : `${Math.round(hours / 24)} days ago`;
  return {
    text: `Figures built ${when}${s.running ? ' · refreshing now…' : ''}${s.lastFailed ? ' · the last refresh failed' : ''}`,
    stale: hours > 36 || Boolean(s.lastFailed),
  };
}

export const formatRate = pc;
